// Audit trail panel for Deep Research. Renders the latest entries with hash,
// kind, latency and a hover JSON peek.

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuditTrail } from "@/hooks/useAuditTrail";
import { cn } from "@/lib/utils";

const KIND_COLOR: Record<string, string> = {
  "plan.create": "text-primary",
  "plan.expand": "text-primary",
  "swarm.spawn": "text-secondary",
  "tool.exec": "text-info",
  "memory.read": "text-muted-foreground",
  "memory.write": "text-success",
  "thought.commit": "text-success",
  "critique": "text-warning",
  "self_consistency": "text-secondary",
  "synthesis": "text-accent",
  "governance.check": "text-success",
  "guardrail.trip": "text-destructive",
  "pause": "text-warning",
  "resume": "text-primary",
  "rollback": "text-warning",
  "stop": "text-destructive",
};

export function ResearchAuditPanel() {
  const entries = useAuditTrail(120);
  const [openId, setOpenId] = useState<string | null>(null);

  if (!entries.length) {
    return (
      <div className="text-[10px] font-mono text-muted-foreground italic">No audit events yet.</div>
    );
  }
  return (
    <div className="space-y-0.5 max-h-[260px] overflow-y-auto pr-1">
      <AnimatePresence initial={false}>
        {entries.map((e) => {
          const open = openId === e.id;
          const time = new Date(e.ts).toLocaleTimeString("en-US", { hour12: false });
          return (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.18 }}
              className="group"
            >
              <button
                onClick={() => setOpenId(open ? null : e.id)}
                className={cn(
                  "w-full text-left text-[10px] font-mono flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-surface-3/60",
                  open && "bg-surface-3/60",
                )}
                title={e.hash}
              >
                <span className="text-muted-foreground tabular-nums">{time}</span>
                <span className={cn("uppercase", KIND_COLOR[e.kind] ?? "text-foreground")}>
                  {e.kind}
                </span>
                {e.tool && <span className="text-info">·{e.tool}</span>}
                {typeof e.latencyMs === "number" && (
                  <span className="text-muted-foreground ml-auto">{e.latencyMs}ms</span>
                )}
                <span className="text-muted-foreground/60 ml-1 shrink-0">{e.hash.slice(0, 6)}</span>
              </button>
              {open && (
                <pre className="mx-1 my-1 px-2 py-1 rounded bg-surface-3/80 border border-border text-[10px] font-mono text-muted-foreground whitespace-pre-wrap break-words max-h-[160px] overflow-auto">
{JSON.stringify({ kind: e.kind, hash: e.hash, agentId: e.agentId, nodeId: e.nodeId, tool: e.tool, latencyMs: e.latencyMs, inputs: e.inputs, note: e.note }, null, 2)}
                </pre>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
